import { useContext } from 'react';
import { AuthContext, useAuth } from '../contexts/AuthContext';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setUser, clearAuth } from '../store';

/**
 * Hook hybride pour la migration progressive de AuthContext vers Redux
 * 
 * Permet de maintenir la compatibilité avec l'ancien AuthContext
 * tout en utilisant Redux pour le state management.
 * 
 * Phase de migration :
 * 1. Hook hybride (ACTUEL) - Utilise Context + Redux en parallèle
 * 2. Migration progressive - Remplace Context par Redux composant par composant  
 * 3. Suppression Context - Une fois tous les composants migrés
 */
export const useAuthHybrid = () => {
  // État depuis Redux (nouvelle source de vérité)
  const reduxAuth = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();
  
  // État depuis Context (legacy, pour compatibilité)
  const contextAuth = useContext(AuthContext);
  
  // Synchronisation Redux → Context si nécessaire
  // (pour les composants qui n'ont pas encore migré)
  const syncToContext = (user) => {
    if (contextAuth?.setUser && user !== contextAuth.user) {
      contextAuth.setUser(user);
    }
  };
  
  // API unifiée qui abstrait la source de données
  const hybridAuth = {
    // Données - priorité à Redux
    user: reduxAuth.user || contextAuth?.user || null,
    isAuthenticated: reduxAuth.isAuthenticated || contextAuth?.isAuthenticated || false,
    isLoading: reduxAuth.isLoading || contextAuth?.isLoading || false,
    error: reduxAuth.error || contextAuth?.error || null,
    token: reduxAuth.token || null,
    
    // Actions - dirigées vers Redux
    login: async (credentials) => {
      try {
        // TODO: Implémenter loginUser de Redux
        // const result = await dispatch(loginUser(credentials)).unwrap();
        // syncToContext(result.user);
        // return result;
        
        // Fallback temporaire vers Context
        if (contextAuth?.login) {
          return await contextAuth.login(credentials);
        }
        throw new Error('Méthode de connexion non disponible');
      } catch (error) {
        console.error('Erreur de connexion hybride:', error);
        throw error;
      }
    },
    
    logout: () => {
      // Redux
      dispatch(clearAuth());
      
      // Context (pour compatibilité)
      if (contextAuth?.logout) {
        contextAuth.logout();
      }
    },
    
    updateUser: (userData) => {
      // Redux
      dispatch(setUser(userData));
      
      // Synchronisation Context
      syncToContext(userData);
    },
    
    // Méta-informations pour le debugging
    __source: {
      redux: !!reduxAuth.user,
      context: !!contextAuth?.user,
      priority: 'redux'
    }
  };
  
  return hybridAuth;
};

/**
 * Hook pour migrer progressivement un composant vers Redux pur
 * 
 * Utilise uniquement Redux, destiné aux nouveaux composants
 * ou composants déjà migrés.
 */
export const useAuthRedux = () => {
  const auth = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();
  
  return {
    ...auth,
    dispatch,
    // Actions prêtes à utiliser
    // login: (credentials) => dispatch(loginUser(credentials)),
    logout: () => dispatch(clearAuth()),
    updateUser: (user) => dispatch(setUser(user)),
  };
};

/**
 * Hook pour les composants legacy qui ne peuvent pas encore migrer
 * 
 * Utilise uniquement le Context existant.
 */
export const useAuthLegacy = () => {
  const auth = useContext(AuthContext);
  
  if (!auth) {
    throw new Error('useAuthLegacy doit être utilisé dans un AuthProvider');
  }
  
  return auth;
};
